import { Card } from "@/components/ui/card";
import { money } from "@/lib/api";
import { CurrencyDollar, Gauge, Robot, SpeakerHigh, Microphone } from "@phosphor-icons/react";

const ROWS = [
  {
    key: "call_limit_price_minor",
    label: "Concurrent Call Limit Price",
    unit: "/ channel",
    hint: "Price per concurrent call channel",
    icon: Gauge,
  },
  {
    key: "ai_model_price_minor",
    label: "AI Model per min price",
    unit: "/ min",
    hint: "Per-minute AI model usage rate",
    icon: Robot,
  },
  {
    key: "tts_price_minor",
    label: "TTS per min price",
    unit: "/ min",
    hint: "Per-minute text-to-speech rate",
    icon: SpeakerHigh,
    freeKey: "tts_billing_type",
  },
  {
    key: "stt_price_minor",
    label: "STT per min price",
    unit: "/ min",
    hint: "Per-minute speech-to-text rate",
    icon: Microphone,
    freeKey: "stt_billing_type",
  },
];

export default function AssignedPricing({
  pricing,
  loading = false,
  title = "Your assigned pricing",
  subtitle = "Rates assigned to this account. Contact your administrator to change them.",
  testid = "assigned-pricing",
}) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <div>
        <h2 className="font-semibold text-base flex items-center gap-2">
          <CurrencyDollar className="w-5 h-5 text-primary" weight="duotone" />
          {title}
        </h2>
        {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        {pricing?.call_limit_pricing_mode && (
          <p className="text-xs text-muted-foreground mt-1 capitalize">
            Pricing mode: {pricing.call_limit_pricing_mode}
          </p>
        )}
      </div>
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading prices…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
          {ROWS.map((row) => {
            const Icon = row.icon;
            const isFree = row.freeKey && pricing?.[row.freeKey] === "free";
            const value = pricing?.[row.key];
            return (
              <Card key={row.key} className="p-4 rounded-xl border-border" data-testid={`${testid}-${row.key}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${
                    isFree ? "bg-emerald-500/15" : "bg-accent"
                  }`}>
                    <Icon className={`w-4 h-4 ${isFree ? "text-emerald-600 dark:text-emerald-400" : "text-primary"}`} weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-muted-foreground leading-snug">{row.label}</p>
                    <p className={`text-xl font-semibold tabular-nums mt-1 ${
                      isFree ? "text-emerald-700 dark:text-emerald-400" : ""
                    }`}>
                      {isFree ? (
                        <>
                          Free
                          <span className="text-xs font-normal text-muted-foreground ml-1">/ min</span>
                        </>
                      ) : (
                        <>
                          {value == null ? "Not set" : money(value)}
                          {value != null && (
                            <span className="text-xs font-normal text-muted-foreground ml-1">{row.unit}</span>
                          )}
                        </>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {isFree ? "Assigned free engine — no per-minute charge" : row.hint}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </section>
  );
}
